/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, observable } from 'mobx';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { OmitFunctions } from 'src/core/stores/types';
import { CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextEntityStore } from 'src/customText/core/stores/CustomTextEntityStore';
import { CustomTextGptPredictedDocument, GptEntityLabel } from 'src/customText/core/stores/CustomTextGptPredictedDocument';

export type CustomTextGptPredictionsStoreData = OmitFunctions<CustomTextGptPredictionsStore>;

export class CustomTextGptPredictionsStore {
    @observable public isDirty: boolean;

    @observable public classes: CustomTextClassStore[];

    @observable public entities: CustomTextEntityStore[];

    @observable.shallow public documents: CustomTextGptPredictedDocument[];

    public tracker: ProgressTrackerStore;

    constructor(data?: CustomTextGptPredictionsStoreData) {
        this.classes = data?.classes ?? [];
        this.entities = data?.entities ?? [];
        this.isDirty = data?.isDirty ?? false;
        this.documents = data?.documents ?? [];
        this.tracker = new ProgressTrackerStore();
    }

    @action
    public setIsDirty(isDirty: boolean) {
        this.isDirty = isDirty;
    }

    @action
    public setEntities(entities: CustomTextEntityStore[]) {
        this.entities = entities;
    }

    @action
    public setClasses(classes: CustomTextClassStore[]) {
        this.classes = classes;
    }

    @action
    public setDocuments(documents: CustomTextGptPredictionsStoreData['documents']) {
        this.documents = documents;
    }

    @action
    public setDocumentAcceptedEntityLabels(documentName: string, entities: GptEntityLabel[]) {
        this.isDirty = true;
        this.documents.find(d => d.name === documentName).setAcceptedEntityLabels(entities);
    }

    @action
    public setDocumentRejectedEntityLabels(documentName: string, entities: GptEntityLabel[]) {
        this.isDirty = true;
        this.documents.find(d => d.name === documentName).setRejectedEntityLabels(entities);
    }

    @action
    public setDocumentAcceptedClassLabels(documentName: string, classes: CustomTextClassStore[]) {
        this.isDirty = true;
        this.documents.find(d => d.name === documentName).setAcceptedClassLabels(classes);
    }

    @action
    public setDocumentRejectedClassLabels(documentName: string, classes: CustomTextClassStore[]) {
        this.isDirty = true;
        this.documents.find(d => d.name === documentName).setRejectedClassLabels(classes);
    }
}
